import * as React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import DropdownTreeSelect from 'react-dropdown-tree-select';
import 'react-dropdown-tree-select/dist/styles.css';
import { TabulatorFull as Tabulator } from "tabulator-tables";
import { Col, Label, Row } from 'reactstrap';
import { useAppDispatch, useAppSelector } from '../../hooks/index';
import { Concept } from '../../redux/ConceptSlice';
import { Node, selectUnitHierarchy } from '../../redux/EntitiesSlice';
import { isAdmin } from '../../redux/MiscSlice';
import { loadDefinitionNetworkRoot, Network, NetworkDetailsRequest, selectDefinitionNetworkRoot, selectDefinitionNetworks } from '../../redux/NetworkSlice';
import { getTaxonomyChanges, TaxonomyChangesState } from '../../redux/TaxonomyChangesSlice';
import { DtsConcept, getSelectedTaxonomyConcept, resetTaxonomyConceptState } from '../../redux/TaxonomyConceptSlice';
import { clearSelectedReference, loadAllTaxonomyReferences } from '../../redux/TaxonomyReferenceSlice';
import { ActiveTaxonomyVersionInfo, selectCurrentVersion } from '../../redux/TaxonomyVersionSlice';
import { StyledSelectableListItem } from '../core/ConceptManagementCore';

interface MemberTableData {
    name: string;
    code: string;
    documentation: string;
}

const getNodeIcon = function (concept: Concept | null): string {
    if (!concept) {
        return "";
    }
    if (concept.code < "2") {
        return ""; // county
    }
    if (concept.code < "3") {
        return ""; // city
    }
    if (concept.code < "5") {
        return ""; // special district
    }
    if (concept.code < "9") {
        return ""; // other entity
    }
    return ""; // organizing node
}

export const addNode = function (parent: StyledSelectableListItem | null, n: Node): StyledSelectableListItem {
    var nodeLabel = n.concept ? n.concept.name : n.name;
    var nvalue = n.concept ? n.concept.id : n.id;
    var listItem: StyledSelectableListItem = {
        networkid: parent ? parent.networkid : "",
        parentItem: null,
        label: nodeLabel,
        value: nvalue,
        disabled: false,
        checked: false,
        className: getNodeIcon(n.concept),
        children: []
    }
    if (parent) {
        parent.children.push(listItem);
    }
    if (n.outgoing) {
        n.outgoing.map(e => addNode(listItem, e.to));
    }
    return listItem;
}

const AxisBuilder = () => {
    const dispatch = useAppDispatch();
    const isAdminUser: boolean = useAppSelector(isAdmin);
    var versionedConcept: DtsConcept | null = useAppSelector(getSelectedTaxonomyConcept);
    var changes: TaxonomyChangesState | null = useAppSelector(getTaxonomyChanges);
    var versionInfo: ActiveTaxonomyVersionInfo | null = useAppSelector(selectCurrentVersion);
    var unitHierarchy: Node | null = useAppSelector(selectUnitHierarchy);
    var definitionNetworks: Network[] = useAppSelector(selectDefinitionNetworks);
    var selectedNetworkRoot: Node | null = useAppSelector(selectDefinitionNetworkRoot);

    var concept: Concept | null = versionedConcept ? versionedConcept.concept : null;
    const [versionYear, setVersionYear] = useState(versionInfo ? versionInfo.year : 2022);
    var memberTabulatorRef = React.useRef<any>(null);


    const getAxisList = function (nlist: Network[]): StyledSelectableListItem[] {
        var alist: Array<StyledSelectableListItem> = [];
        if (nlist == null) {
            return [];
        }
        for (var net of nlist) {
            for (var root of net.roots) {
                if (root.concept.name === "Line items") {
                    continue;
                }
                var listItem: StyledSelectableListItem = {
                    networkid: net.name,
                    parentItem: null,
                    className: "",
                    label: root.concept.name + " (" + net.name + ")",
                    value: net.name + "#" + root.concept.id,
                    disabled: false,
                    checked: false,
                    children: []                    
                }
                alist.push(listItem);
            }
        }
        alist.sort((a, b) => a.label.localeCompare(b.label));
        return alist;
    }
    const [axisList, setAxisList] = useState<StyledSelectableListItem[]>(getAxisList(definitionNetworks));


    const ddhandleAxisChange = function (currentNode: any, selectedNodes: any) {
        if (!selectedNodes || selectedNodes.length === 0 || !versionInfo) {
            return;
        }
        var selection = selectedNodes[0];
        var parts: string[] = selection.value.split("#");
        var req: NetworkDetailsRequest = {
            networkId: parts[0],
            nodeId: parts[1],
            taxonomyInfo: versionInfo
        }
        dispatch(loadDefinitionNetworkRoot(req));

    }

    const handleCellEdited = function (cell: any) {
    }

    // handle the logic to add a new account 
    const addMemberTitleFormatter = function (cell: any, formatterParams: any, onRendered: any) {

        //build dropdown
        var button = document.createElement("div");

        button.innerHTML = "<i class='bi bi-plus-circle' title='Add new Member...'></i>  Member";

        button.onclick = function () {
            // add code here

        }

        return button;
    }

    var defaultColumns = [
        //        { rowHandle: true, formatter: "handle", field: "buttonhandler", headerSort: false, width: 45, minWidth: 45, titleFormatter: addPartTitleFormatter },
        { title: "Member name", field: "name", width: 280, titleFormatter: addMemberTitleFormatter },
        { title: "Code", field: "code", width: 80, editor: "input"   },
        { title: "Documentation", field: "documentation", editor: "textarea" }
    ];

    const convertMemberNodeToTableData = function (memberNode: Node): MemberTableData {
        var member = memberNode.concept;
        if (!member) {
            return { name: "unkown", code: "", documentation: "" };
        }
        var tdataRow = {
            name: member.name,
            code: member.code,
            documentation: member.documentation
        }
        return tdataRow;
    }
    const getMemberData = function (root: Node | null): MemberTableData[] {
        var results: MemberTableData[] = [];
        if (root) {
            root.outgoing.map(o => results.push(convertMemberNodeToTableData(o.to)));
        }
        return results;

    }
    const [tableData, setTableData] = useState<MemberTableData[]>([]);


    useEffect(() => {
        dispatch(clearSelectedReference());
    }, [versionedConcept]);

    useEffect(() => {
        setTableData(getMemberData(selectedNetworkRoot));
    }, [selectedNetworkRoot]);

    const secondElementStyle = {
        marginTop: '4pt',
        marginLeft: '1em'
    }
    const editButtonStyle = {
        marginTop: '4pt',
        marginLeft: '1em',
        marginBottom: '4pt'
    }
    const leftMarginStyle = {
        marginLeft: '1em'
    }
    const selRefStyle = {
        marginTop: '8pt',
        marginLeft: '1em'
    }
    const rightAlignStyle = {
        align: 'right'
    }
    const flexRow = {
        display: 'flex'
    }
    const flexRightChild = {
        marginLeft: 'auto',
        marginRight: '1em'
    }
    const axisSelector = useMemo(() => {
        return (
            <DropdownTreeSelect
                id="unitPicker"
                mode="radioSelect"
                data={axisList}
                keepTreeOnSearch={true} keepChildrenOnSearch={true}
                onChange={ddhandleAxisChange}
                texts={{ placeholder: "Choose Network..." }} />
        )
    }, [unitHierarchy, versionInfo]);

    React.useEffect(() => {

        memberTabulatorRef.current = new Tabulator('#axisBuilder', {
            data: tableData,
            columns:  defaultColumns,
            layout: "fitColumns",
            cellEdited: handleCellEdited,
        });

    }, [, ddhandleAxisChange])

    return (
        <React.Fragment>
            <Row>
                <Col sm="8">
                    <Row style={{width: "100%"}}>
                        <Col className="text-md-right" >
                            <Label><b>Breakdown (by network):</b></Label>
                        </Col>
                        <Col md="9" >
                            <Col md="9">
                                <h5 style={secondElementStyle}>
                                    {axisSelector}
                                </h5>
                            </Col>
                            <div id="axisBuilder" ref={ele => { memberTabulatorRef.current = ele }} />
                        </Col>
                    </Row>
                </Col>
            </Row>
        </React.Fragment>
    );
}
export default AxisBuilder;